import * as React from 'react';
import {
  Outlet,
  createRootRoute,
  HeadContent,
  Scripts,
} from '@tanstack/react-router';
import { ClerkProvider } from '@clerk/tanstack-react-start';
import { ConvexProviderWithClerk } from 'convex/react-clerk';
import { ConvexProvider, ConvexReactClient } from 'convex/react';
import { useAuth } from '@clerk/tanstack-react-start';
import {
  ThemeProvider,
  ToastProvider,
  TooltipProvider,
} from '@fragments-sdk/ui';
import '@fontsource-variable/plus-jakarta-sans';
import globalsCss from '../styles/globals.scss?url';
import { initSentry, captureError } from '@/lib/sentry';
import { DevTools } from '@/components/DevTools';
import { isAppSite } from '@/lib/seo';

// Initialize Sentry as early as possible
initSentry();

const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL);
const TEST_CLERK_USER_STORAGE_KEY = '__fragments_test_clerk_user__';
const TEST_CLERK_USER_CHANGED_EVENT = 'fragments:test-clerk-user';
const TEST_MODE_ACTIVE =
  import.meta.env.DEV && import.meta.env.VITE_FRAGMENTS_TEST_MODE === '1';

function hasTestClerkUser(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === 'undefined') return false;
  if (new URLSearchParams(window.location.search).has('__test_user')) {
    return true;
  }
  return Boolean(
    window.sessionStorage.getItem(TEST_CLERK_USER_STORAGE_KEY) ??
    window.localStorage.getItem(TEST_CLERK_USER_STORAGE_KEY),
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: 'Fragments | Design System Governance for AI UI' },
      {
        name: 'description',
        content:
          'Govern design systems, AI-generated UI, components, tokens, and findings in one place.',
      },
      {
        name: 'robots',
        content: isAppSite() ? 'noindex, nofollow' : 'index, follow',
      },
    ],
    links: [
      { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
      {
        rel: 'apple-touch-icon',
        href: '/fragments-logo.png',
      },
      { rel: 'stylesheet', href: globalsCss },
    ],
  }),
  component: RootComponent,
  errorComponent: RootErrorBoundary,
  shellComponent: RootDocument,
});

function RootErrorBoundary({ error }: { error: Error }) {
  // Report to Sentry
  const errorId = React.useMemo(() => {
    captureError(error, { boundary: 'RootErrorBoundary' });
    return crypto.randomUUID().slice(0, 8);
  }, [error]);

  const isNetworkError =
    error.message.includes('fetch') ||
    error.message.includes('network') ||
    error.message.includes('Failed to fetch') ||
    !navigator.onLine;

  // Detect dark mode — this renders outside ThemeProvider so we can't use CSS vars
  const [isDark, setIsDark] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDark(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const bg = isDark ? '#09090b' : '#ffffff';
  const fg = isDark ? '#fafafa' : '#09090b';
  const fgSecondary = isDark ? '#a1a1aa' : '#71717a';
  const fgTertiary = isDark ? '#71717a' : '#a1a1aa';
  const border = isDark ? '#27272a' : '#e4e4e7';
  const preBg = isDark ? '#18181b' : '#f4f4f5';
  const btnPrimaryBg = isDark ? '#fafafa' : '#18181b';
  const btnPrimaryFg = isDark ? '#09090b' : '#ffffff';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        padding: '24px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        gap: '16px',
        background: bg,
        color: fg,
      }}
    >
      <h1
        style={{
          fontSize: '72px',
          fontWeight: 700,
          margin: 0,
          lineHeight: 1,
          letterSpacing: '-0.02em',
          color: fg,
        }}
      >
        {isNetworkError ? 'Offline' : '500'}
      </h1>
      <h2 style={{ fontSize: '20px', fontWeight: 600, margin: 0, color: fg }}>
        {isNetworkError ? 'You appear to be offline' : 'Something went wrong'}
      </h2>
      <p
        style={{
          fontSize: '14px',
          color: fgSecondary,
          margin: 0,
          maxWidth: '480px',
          textAlign: 'center',
        }}
      >
        {isNetworkError
          ? 'Check your internet connection and try again.'
          : 'An unexpected error occurred. Please try refreshing the page.'}
      </p>
      {process.env.NODE_ENV === 'development' && (
        <pre
          style={{
            fontSize: '12px',
            background: preBg,
            color: fgSecondary,
            padding: '12px 16px',
            borderRadius: '8px',
            border: `1px solid ${border}`,
            maxWidth: '600px',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {error.message}
        </pre>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: 500,
            borderRadius: '6px',
            border: `1px solid ${border}`,
            background: 'transparent',
            color: fg,
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => (window.location.href = '/')}
          style={{
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: 500,
            borderRadius: '6px',
            border: 'none',
            background: btnPrimaryBg,
            color: btnPrimaryFg,
            cursor: 'pointer',
          }}
        >
          Go to Dashboard
        </button>
      </div>
      <p
        style={{
          fontSize: '11px',
          color: fgTertiary,
          margin: 0,
          fontFamily: 'ui-monospace, monospace',
        }}
      >
        Error ID: {errorId}
      </p>
    </div>
  );
}

function RootComponent() {
  const [hasFixtureUser, setHasFixtureUser] = React.useState(hasTestClerkUser);
  React.useEffect(() => {
    if (!import.meta.env.DEV) return;
    const update = () => setHasFixtureUser(hasTestClerkUser());
    update();
    const interval = window.setInterval(update, 100);
    window.addEventListener(TEST_CLERK_USER_CHANGED_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener(TEST_CLERK_USER_CHANGED_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);
  const shouldUseTestConvex = TEST_MODE_ACTIVE || hasFixtureUser;
  const app = (
    <>
      <Outlet />
      <DevTools />
    </>
  );

  return (
    <ThemeProvider defaultMode="system">
      <TooltipProvider>
        <ToastProvider position="bottom-right">
          <ClerkProvider>
            {shouldUseTestConvex ? (
              <ConvexProvider client={convex}>{app}</ConvexProvider>
            ) : (
              <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
                {app}
              </ConvexProviderWithClerk>
            )}
          </ClerkProvider>
        </ToastProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}
